import * as React from 'react';
import { TableRow, TableCell, Collapse, Box, Typography, Select, MenuItem, Checkbox, FormControlLabel } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

import { Question } from '../../../../Models/Assessment/AssessmentCategory.model';
import { SelectionType } from '../../../../Enum/SelectionTypeEnum';
import DebouncedTextField from './DebouncedTextField';

interface SubOptionsRowProps {
    question: Question;
    qIndex: number;
    colSpan: number;
    onUpdateSubAnswer: (isSelected: boolean, qIndex: number, oIndex: number, sIndex: number) => void;
    onUpdateSubOptionName: (name: string, qIndex: number, oIndex: number, sIndex: number) => void;
    onUpdateSubSubAnswer: (isSelected: boolean, qIndex: number, oIndex: number, sIndex: number, ssIndex: number) => void;
    onUpdateSubSubOptionName: (name: string, qIndex: number, oIndex: number, sIndex: number, ssIndex: number) => void;
    onUpdateSubOptionSubQuestionAnswerText: (value: string | Date | null, qIndex: number, oIndex: number, sIndex: number, sqIndex: number) => void;
    onUpdateSubOptionSubQuestionAnswer: (isSelected: boolean, qIndex: number, oIndex: number, sIndex: number, sqIndex: number, sqOptionIndex: number) => void;
}

const SubOptionsRow: React.FC<SubOptionsRowProps> = React.memo(
    ({
        question,
        qIndex,
        colSpan,
        onUpdateSubAnswer,
        onUpdateSubOptionName,
        onUpdateSubSubAnswer,
        onUpdateSubSubOptionName,
        onUpdateSubOptionSubQuestionAnswerText,
        onUpdateSubOptionSubQuestionAnswer,
    }) => {
        // Find the first selected option that has SubOptions
        const selectedOptionWithSubOptions = question?.Options?.find((opt) => opt.IsSelected && opt.SubOptions?.length > 0);

        if (!selectedOptionWithSubOptions) return null;

        const oIndex = question.Options.indexOf(selectedOptionWithSubOptions);

        return (
            <TableRow key={`${question.Id}_suboptions_${qIndex}`}>
                <TableCell colSpan={colSpan} sx={{ py: 1, backgroundColor: '#fafafa' }}>
                    <Collapse in={true} timeout="auto">
                        <Box
                            sx={{
                                pl: 5,
                                display: 'flex',
                                flexDirection: 'column',
                                flexWrap: 'wrap',
                                gap: 2,
                                justifyContent: 'center',
                            }}
                        >
                            {selectedOptionWithSubOptions.SubOptions.map((subOption, sIndex) => {
                                const subOptionSelectionType = subOption.SelectionType || SelectionType.SingleSelect;

                                return (
                                    <Box key={`sub_option_${subOption.Id || sIndex}_${question.Id}`} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {(subOptionSelectionType === SelectionType.SingleSelect || subOptionSelectionType === SelectionType.MultiSelect) && (
                                            <FormControlLabel
                                                sx={{ margin: 0 }}
                                                control={
                                                    <Checkbox
                                                        checked={subOption.IsSelected || false}
                                                        size="small"
                                                        onChange={(e) => onUpdateSubAnswer(e.target.checked, qIndex, oIndex, sIndex)}
                                                        {...(subOptionSelectionType === SelectionType.SingleSelect
                                                            ? { icon: <PanoramaFishEyeIcon />, checkedIcon: <RadioButtonCheckedIcon /> }
                                                            : {})}
                                                    />
                                                }
                                                label={subOption.Name}
                                            />
                                        )}
                                        {subOptionSelectionType === SelectionType.Text && (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {subOption.Name}
                                                </Typography>
                                                <DebouncedTextField
                                                    size="small"
                                                    fullWidth
                                                    placeholder="Enter text..."
                                                    value={subOption.TextAnswer || ''}
                                                    onChange={(val) => onUpdateSubOptionName(val, qIndex, oIndex, sIndex)}
                                                />
                                            </Box>
                                        )}

                                        {/* Render SubQuestions of SubOption if SubOption is selected and has SubQuestions */}
                                        {subOption.IsSelected && subOption.SubQuestions && subOption.SubQuestions.length > 0 && (
                                            <Box sx={{ pl: 4, mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {subOption.SubQuestions.map((subQuestion, sqIndex) => {
                                                    const selectionType = subQuestion.SelectionType || SelectionType.Text;

                                                    return (
                                                        <Box
                                                            key={`sub_opt_sub_q_${subQuestion.Id || sqIndex}`}
                                                            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                                                        >
                                                            {selectionType === SelectionType.Text && (
                                                                <>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {subQuestion.Description}
                                                                    </Typography>
                                                                    <DebouncedTextField
                                                                        size="small"
                                                                        fullWidth
                                                                        placeholder={subQuestion.Description || ''}
                                                                        value={subQuestion.AnswerText || ''}
                                                                        onChange={(val) =>
                                                                            onUpdateSubOptionSubQuestionAnswerText(val, qIndex, oIndex, sIndex, sqIndex)
                                                                        }
                                                                    />
                                                                </>
                                                            )}

                                                            {selectionType === SelectionType.DateTime && (
                                                                <>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {subQuestion.Description}
                                                                    </Typography>
                                                                    <DesktopDatePicker
                                                                        format="MM/dd/yyyy"
                                                                        value={subQuestion.AnswerText ? new Date(subQuestion.AnswerText) : null}
                                                                        onChange={(newValue) =>
                                                                            onUpdateSubOptionSubQuestionAnswerText(newValue, qIndex, oIndex, sIndex, sqIndex)
                                                                        }
                                                                        slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }}
                                                                    />
                                                                </>
                                                            )}

                                                            {(selectionType === SelectionType.SingleSelect || selectionType === SelectionType.MultiSelect) && (
                                                                <>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {subQuestion.Description}
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                                                        {subQuestion.Options?.map((sqOption, sqOptionIndex) => (
                                                                            <FormControlLabel
                                                                                key={`sub_opt_sub_q_opt_${sqOption.Id || sqOptionIndex}`}
                                                                                sx={{ margin: 0 }}
                                                                                control={
                                                                                    <Checkbox
                                                                                        checked={sqOption.IsSelected || false}
                                                                                        size="small"
                                                                                        onChange={(e) =>
                                                                                            onUpdateSubOptionSubQuestionAnswer(
                                                                                                e.target.checked,
                                                                                                qIndex,
                                                                                                oIndex,
                                                                                                sIndex,
                                                                                                sqIndex,
                                                                                                sqOptionIndex
                                                                                            )
                                                                                        }
                                                                                        {...(selectionType === SelectionType.SingleSelect
                                                                                            ? {
                                                                                                  icon: <PanoramaFishEyeIcon />,
                                                                                                  checkedIcon: <RadioButtonCheckedIcon />,
                                                                                              }
                                                                                            : {})}
                                                                                    />
                                                                                }
                                                                                label={sqOption.Name}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                </>
                                                            )}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}

                                        {/* Render SubOptions of SubOption if SubOption is selected and has SubOptions */}
                                        {subOption.IsSelected && subOption.SubOptions && subOption.SubOptions.length > 0 && (
                                            <Box sx={{ pl: 4, mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {subOption.SubOptions.map((subSubOption, ssIndex) => {
                                                    const subSubOptionSelectionType = subSubOption.SelectionType || SelectionType.SingleSelect;
                                                    return (
                                                        <Box key={`sub_sub_option_${subSubOption.Id || ssIndex}_${subOption.Id}`}>
                                                            {(subSubOptionSelectionType === SelectionType.SingleSelect ||
                                                                subSubOptionSelectionType === SelectionType.MultiSelect) && (
                                                                <FormControlLabel
                                                                    sx={{ margin: 0 }}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={subSubOption.IsSelected || false}
                                                                            size="small"
                                                                            onChange={(e) =>
                                                                                onUpdateSubSubAnswer(e.target.checked, qIndex, oIndex, sIndex, ssIndex)
                                                                            }
                                                                            {...(subSubOptionSelectionType === SelectionType.SingleSelect
                                                                                ? { icon: <PanoramaFishEyeIcon />, checkedIcon: <RadioButtonCheckedIcon /> }
                                                                                : {})}
                                                                        />
                                                                    }
                                                                    label={subSubOption.Name}
                                                                />
                                                            )}
                                                            {subSubOptionSelectionType === SelectionType.Text && (
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {subSubOption.Name}
                                                                    </Typography>
                                                                    <DebouncedTextField
                                                                        size="small"
                                                                        fullWidth
                                                                        placeholder="Enter text..."
                                                                        value={subSubOption.TextAnswer || ''}
                                                                        onChange={(val) => onUpdateSubSubOptionName(val, qIndex, oIndex, sIndex, ssIndex)}
                                                                    />
                                                                </Box>
                                                            )}
                                                            {(subSubOptionSelectionType === SelectionType.DropdownWithSingleSelect ||
                                                                subSubOptionSelectionType === SelectionType.MultiSelectWithDropdown) && (
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {subSubOption.Name}
                                                                    </Typography>
                                                                    <Select
                                                                        size="small"
                                                                        fullWidth
                                                                        value={subSubOption.IsSelected ? subSubOption.Name : ''}
                                                                        onChange={(e) => onUpdateSubSubAnswer(true, qIndex, oIndex, sIndex, ssIndex)}
                                                                    >
                                                                        <MenuItem value="">
                                                                            <em>None</em>
                                                                        </MenuItem>
                                                                        <MenuItem value={subSubOption.Name}>{subSubOption.Name}</MenuItem>
                                                                    </Select>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        );
    }
);

SubOptionsRow.displayName = 'SubOptionsRow';

export default SubOptionsRow;
